import React from 'react';
import { formatNumber, timeOfDay } from '../format';

/**
 * What happened to one import.
 *
 * Built from the import record itself rather than an event table: those are the
 * only moments we actually record, and a timeline that invents the rest would
 * be worse than a short one.
 */
function entriesFor(record) {
    const entries = [];

    // Keyed off the status, not the message: a batch can fail without one, and
    // a log that then says nothing about it is the least useful moment to be
    // silent.
    if (record.status === 'failed' || record.error) {
        entries.push({
            dot: 'bad',
            title: 'Import failed',
            detail: record.error || 'The fetch did not finish. No reason was recorded.',
        });
    }

    if (record.status === 'completed') {
        entries.push({
            dot: 'good',
            title: 'Import applied',
            detail: (formatNumber(record.imported_count) || '0') + ' rows written to iCLIMB.',
        });
    }

    if (record.pending_count > 0) {
        entries.push({
            dot: 'warn',
            title: 'Awaiting approval',
            detail: formatNumber(record.pending_count) + ' rows are waiting for a decision.',
        });
    }

    // What the filter left out, said plainly. Otherwise a ZIP of 3,477 parcels
    // that queued 509 of them reads as an import that lost most of its work.
    if (!record.include_residential && record.total_found > 0) {
        const kept = (record.new_count || 0) + (record.matched_count || 0);

        entries.push({
            dot: 'info',
            title: 'Houses left out',
            detail: 'Single family homes were skipped, so ' + formatNumber(kept) + ' of ' +
                formatNumber(record.total_found) + ' parcels reached the queue. ' +
                'Apartment blocks were kept.',
        });
    }

    if (record.total_found > 0) {
        // total_found is what the ZIP was found to hold, counted before any of
        // it was fetched. Calling that "retrieved" on a job that failed or is
        // still running claims work that has not happened.
        const finished = record.status === 'ready_for_review' || record.status === 'completed';

        entries.push({
            dot: 'info',
            title: finished ? 'Fetch complete' : 'Fetch started',
            detail: finished
                ? formatNumber(record.total_found) + ' parcels scanned · ' +
                    (formatNumber(record.new_count) || '0') + ' new · ' +
                    (formatNumber(record.matched_count) || '0') + ' already in iCLIMB.'
                : formatNumber(record.total_found) + ' parcels found in this ZIP.',
        });
    }

    entries.push({
        dot: 'plain',
        title: 'Job started',
        detail: 'ZIP ' + record.zip + (record.requested_by ? ' · requested by ' + record.requested_by : ''),
        time: timeOfDay(record.created_at),
    });

    return entries;
}

export default function LogStep({ record }) {
    return (
        <div className="Log">
            {entriesFor(record).map((entry, index) => (
                <div key={index} className="Log-row">
                    <span className={'Log-dot' + (entry.dot === 'plain' ? '' : ' Log-dot--' + entry.dot)} />
                    <div className="Log-body">
                        <div className="Log-title">{entry.title}</div>
                        <div className="Log-detail">{entry.detail}</div>
                    </div>
                    {entry.time && <div className="Log-time">{entry.time}</div>}
                </div>
            ))}
        </div>
    );
}
